import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';

interface ConfirmOptions {
  title?: string;
  description?: string;
  content?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'error' | 'primary' | 'secondary' | 'warning' | 'info' | 'success';
  variant?: 'warning' | 'info' | 'question';
}

interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
  resolve?: (value: boolean) => void;
}

/**
 * Custom hook for confirmation dialogs with modern UI
 * 
 * @example
 * ```tsx
 * const { confirm, ConfirmDialog } = useConfirm();
 * 
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: 'Delete Item',
 *     description: 'Are you sure you want to delete this item? This action cannot be undone.',
 *     confirmText: 'Delete',
 *     confirmColor: 'error',
 *     variant: 'warning'
 *   });
 *   
 *   if (confirmed) {
 *     // Do the deletion
 *   }
 * };
 * 
 * return (
 *   <>
 *     <Button onClick={handleDelete}>Delete</Button>
 *     <ConfirmDialog />
 *   </>
 * );
 * ```
 */
export const useConfirm = () => {
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
  });

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        setState({
          isOpen: true,
          ...options,
          resolve,
        });
      });
    },
    []
  );

  const handleClose = useCallback(
    (confirmed: boolean) => {
      state.resolve?.(confirmed);
      setState({
        isOpen: false,
      });
    },
    [state.resolve]
  );

  const getIcon = () => {
    switch (state.variant) {
      case 'warning':
        return <WarningAmberIcon color="warning" sx={{ fontSize: 40 }} />;
      case 'info':
        return <InfoIcon color="info" sx={{ fontSize: 40 }} />;
      case 'question':
      default:
        return <HelpOutlineIcon color="primary" sx={{ fontSize: 40 }} />;
    }
  };

  const ConfirmDialog = useCallback(
    () => (
      <Dialog
        open={state.isOpen}
        onClose={() => handleClose(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {getIcon()}
            <Box>{state.title || 'Confirm Action'}</Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {state.description && (
            <DialogContentText sx={{ mb: state.content ? 2 : 0 }}>
              {state.description}
            </DialogContentText>
          )}
          {state.content}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => handleClose(false)}
            variant="outlined"
            color="inherit"
          >
            {state.cancelText || 'Cancel'}
          </Button>
          <Button
            onClick={() => handleClose(true)}
            variant="contained"
            color={state.confirmColor || 'primary'}
            autoFocus
          >
            {state.confirmText || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [state, handleClose]
  );

  return { confirm, ConfirmDialog };
};

